/**
 * Main App Component with Routing
 * For Media Devoted | Created By Ciro Autiero
 */

import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/hooks';
import { MainLayout } from '@/components/layouts/MainLayout';
import { DropTimelineLauncherProvider } from '@/features/siem/components/drop-timeline';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { LoadingOverlay } from '@/components/ui';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LegacyAutomationRedirect } from '@/pages/admin/drop-generator/country/LegacyAutomationRedirect';

/** Retry lazy import; on chunk load failure (e.g. after deploy), reload to fetch fresh assets. */
function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 2,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const load = (remaining: number): Promise<{ default: T }> =>
      importFn().catch((err) => {
        const msg = getApiErrorMessage(err, '');
        const isChunkError =
          /Failed to fetch dynamically imported module|Loading chunk \d+ failed/i.test(msg);
        if (remaining > 0 && isChunkError) {
          return load(remaining - 1);
        }
        window.location.reload();
        throw err;
      });
    return load(retries);
  });
}

// Lazy loaded pages
const LoginPage = lazyWithRetry(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard/DashboardPage'));
const CampaignsPage = lazyWithRetry(() => import('@/pages/campaigns/CampaignsPage'));
const BanksPage = lazyWithRetry(() => import('@/pages/banks/BanksPage'));
const TrackerSeedPage = lazyWithRetry(() => import('@/pages/tracker-seed/TrackerSeedPage'));
const LanderSeedPage = lazyWithRetry(() => import('@/pages/lander-seed/LanderSeedPage'));
const PathSeedPage = lazyWithRetry(() => import('@/pages/path-seed/PathSeedPage'));
const DropsPage = lazyWithRetry(() => import('@/pages/drops/DropsPage'));
const CreateDropPage = lazyWithRetry(() => import('@/pages/drops/CreateDropPage'));
const DropDetailsPage = lazyWithRetry(() => import('@/pages/drops/DropDetailsPage'));
const FlowsPage = lazyWithRetry(() => import('@/pages/flows/FlowsPage'));
const FlowDetailPage = lazyWithRetry(() => import('@/pages/flows/FlowDetailPage'));
const OptimizerPage = lazyWithRetry(() => import('@/pages/optimizer/OptimizerPage'));
const UsersPage = lazyWithRetry(() => import('@/pages/admin/system/UsersPage'));
const RolesPage = lazyWithRetry(() => import('@/pages/admin/system/RolesPage'));
const ApiKeysPage = lazyWithRetry(() => import('@/pages/admin/system/ApiKeysPage'));
const AuditLogsPage = lazyWithRetry(() => import('@/pages/admin/audit/AuditLogsPage'));
const SiemPage = lazyWithRetry(() => import('@/pages/admin/audit/SiemPage'));
const DropTimelinePage = lazyWithRetry(
  () => import('@/pages/admin/investigation/DropTimelinePage'),
);
const InfrastructurePerformancePage = lazyWithRetry(() => import('@/pages/admin/InfrastructurePerformancePage'));
const ApiCreditsPage = lazyWithRetry(() => import('@/pages/admin/ApiCreditsPage'));
const AiModelsPage = lazyWithRetry(() => import('@/pages/admin/AiModelsPage'));
const SmswiserCatalogPage = lazyWithRetry(() => import('@/pages/admin/smswiser/catalog/SmswiserCatalogPage'));
const SmswiserCatalogDataPage = lazyWithRetry(() => import('@/pages/admin/smswiser/catalog/SmswiserCatalogDataPage'));
const AdminTextsPage = lazyWithRetry(() => import('@/pages/admin/smswiser/texts/AdminTextsPage'));
const FetchLeadsCountPage = lazyWithRetry(() => import('@/pages/admin/smswiser/leads/FetchLeadsCountPage'));
const SmswiserCampaignsPage = lazyWithRetry(() => import('@/pages/admin/smswiser/campaigns/SmswiserCampaignsPage'));
const SmswiserSourcesPage = lazyWithRetry(() => import('@/pages/admin/smswiser/catalog/SmswiserSourcesPage'));
const SmswiserConfigPage = lazyWithRetry(() => import('@/pages/admin/smswiser/config/SmswiserConfigPage'));
const TagRulesPage = lazyWithRetry(() => import('@/pages/admin/TagRulesPage'));
const ListGroupsPage = lazyWithRetry(() => import('@/pages/admin/ListGroupsPage'));
const DropGeneratorPage = lazyWithRetry(
  () => import('@/pages/admin/drop-generator/DropGeneratorPage'),
);
const DropGeneratorCountryPage = lazyWithRetry(
  () => import('@/pages/admin/drop-generator/DropGeneratorCountryPage'),
);
const CampaignTextsPage = lazyWithRetry(() => import('@/pages/admin/CampaignTextsPage'));
const CalendarPage = lazyWithRetry(() => import('@/pages/calendar/CalendarPage'));
const SmswiserPinnedPreparedPage = lazyWithRetry(
  () => import('@/pages/admin/SmswiserPinnedPreparedPage/SmswiserPinnedPreparedPage'),
);
const SmswiserPinnedPresetWizardPage = lazyWithRetry(
  () => import('@/pages/admin/SmswiserPinnedPreparedPage/wizard/SmswiserPinnedPresetWizardPage'),
);

import SmswiserCampaignDetailPage from '@/pages/admin/SmswiserCampaignDetailPage';
const SettingsPage = lazyWithRetry(() => import('@/pages/settings/SettingsPage'));
const DocsPage = lazyWithRetry(() => import('@/pages/docs/DocsPage'));
const ForbiddenPage = lazyWithRetry(() => import('@/pages/ForbiddenPage'));
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <LoadingOverlay open title="Loading TrafficPulse…" description="Preparing your workspace." />
    );
  }

  return (
    <Suspense
      fallback={
        <LoadingOverlay open title="Loading…" description="Fetching the next screen." />
      }
    >
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />
        </Route>

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DropTimelineLauncherProvider>
                <MainLayout />
              </DropTimelineLauncherProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredPermission="dashboard.view">
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Campaigns - Main navigation with sidebar */}
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute requiredRole="admin">
                <CampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId"
            element={
              <ProtectedRoute requiredRole="admin">
                <CampaignsPage />
              </ProtectedRoute>
            }
          />

          {/* Banks (Flow Tree / Paths / Landers / Offers / Network Rules) */}
          <Route
            path="/banks"
            element={
              <ProtectedRoute requiredPermission="banks.view">
                <BanksPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy redirect: the page was historically served at /offer-banks. */}
          <Route
            path="/offer-banks"
            element={<Navigate to="/banks" replace />}
          />

          {/* Offers — pull seed data from a tracker (voluum | binom) */}
          <Route
            path="/admin/offers/:tracker"
            element={
              <ProtectedRoute requiredPermission="offer_banks.manage">
                <TrackerSeedPage />
              </ProtectedRoute>
            }
          />

          {/* Landers — pull seed data from a tracker (voluum | binom) */}
          <Route
            path="/admin/landers/:tracker"
            element={
              <ProtectedRoute requiredPermission="lander_banks.manage">
                <LanderSeedPage />
              </ProtectedRoute>
            }
          />

          {/* Paths — pull seed data from a tracker (voluum | binom) */}
          <Route
            path="/admin/paths/:tracker"
            element={
              <ProtectedRoute requiredPermission="path_banks.manage">
                <PathSeedPage />
              </ProtectedRoute>
            }
          />

          {/* Drops */}
          <Route
            path="/drops"
            element={
              <ProtectedRoute requiredPermission="drops.view">
                <DropsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drops/create"
            element={
              <ProtectedRoute requiredPermission="drops.create">
                <CreateDropPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drops/:uniqueId"
            element={
              <ProtectedRoute requiredPermission="drops.view">
                <DropDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Flows (standalone view) */}
          <Route
            path="/flows"
            element={
              <ProtectedRoute requiredRole="admin">
                <FlowsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/flows/:flowId"
            element={
              <ProtectedRoute requiredRole="admin">
                <FlowDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Scheduler (formerly Optimizer Control Center) */}
          <Route
            path="/scheduler"
            element={
              <ProtectedRoute requiredPermission="flows.view">
                <OptimizerPage />
              </ProtectedRoute>
            }
          />
          {/* Back-compat: the page used to live at /optimizer */}
          <Route
            path="/optimizer"
            element={<Navigate to="/scheduler" replace />}
          />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredPermission="users.view">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute requiredPermission="roles.view">
                <RolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <ProtectedRoute requiredPermission="admin.api_keys">
                <ApiKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute requiredPermission="audit.view">
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/siem"
            element={
              <ProtectedRoute requiredRole="admin">
                <SiemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/drop-timeline"
            element={
              <ProtectedRoute requiredPermission="investigation.drop_timeline">
                <DropTimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/infrastructure"
            element={
              <ProtectedRoute requiredPermission="admin.infrastructure">
                <InfrastructurePerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-credits"
            element={
              <ProtectedRoute requiredPermission="admin.api_credits" requiredRole="admin">
                <ApiCreditsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-models"
            element={
              <ProtectedRoute requiredPermission="admin.ai_models">
                <AiModelsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smswiser-catalog"
            element={
              <ProtectedRoute requiredPermission="smswiser.catalog">
                <SmswiserCatalogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smswiser-catalog-data"
            element={
              <ProtectedRoute requiredPermission="smswiser.catalog">
                <SmswiserCatalogDataPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smswiser-texts"
            element={
              <ProtectedRoute requiredPermission="admin.texts">
                <AdminTextsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fetch-leads-counts"
            element={
              <ProtectedRoute requiredPermission="smswiser.catalog">
                <FetchLeadsCountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smswiser"
            element={
              <ProtectedRoute requiredPermissions={['drops.view', 'stats.phone']}>
                <SmswiserCampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smswiser/pinned-prepared/preset-wizard"
            element={
              <ProtectedRoute requiredPermissions={['drops.view', 'stats.phone']}>
                <SmswiserPinnedPresetWizardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smswiser/pinned-prepared"
            element={
              <ProtectedRoute requiredPermissions={['drops.view', 'stats.phone']}>
                <SmswiserPinnedPreparedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smswiser/:countryKey"
            element={
              <ProtectedRoute requiredPermissions={['drops.view', 'stats.phone']}>
                <SmswiserCampaignDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Calendar Overview */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute requiredPermission="drops.calendar">
                <CalendarPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/smswiser-sources"
            element={
              <ProtectedRoute requiredPermission="smswiser.sources">
                <SmswiserSourcesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/smswiser-config"
            element={
              <ProtectedRoute requiredPermission="smswiser.config">
                <SmswiserConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/list-tag-rules"
            element={
              <Navigate to="/admin/tag-rules?tab=list" replace />
            }
          />
          <Route
            path="/admin/tag-rules"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  'smswiser.list_tag_rules',
                  'smswiser.text_tag_rules',
                  'smswiser.gateway_tag_rules',
                  'smswiser.hour_tag_rules',
                  'smswiser.path_tag_rules',
                  'smswiser.lander_tag_rules',
                  'smswiser.offer_tag_rules',
                  'smswiser.phone_tag_rules',
                  'smswiser.drop_tag_rules',
                  'smswiser.source_tag_rules',
                  'smswiser.flow_optimizer_tag_rules',
                ]}
              >
                <TagRulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/list-groups"
            element={
              <ProtectedRoute requiredPermission="smswiser.list_groups">
                <ListGroupsPage />
              </ProtectedRoute>
            }
          />
          {/* Automation (the Drop Generator); old links redirect, query kept */}
          <Route
            path="/admin/automation"
            element={
              <ProtectedRoute requiredPermission="drop_generator.view">
                <DropGeneratorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/automation/:countryKey"
            element={
              <ProtectedRoute requiredPermission="drop_generator.view">
                <DropGeneratorCountryPage />
              </ProtectedRoute>
            }
          />
          {['/admin/drop-generator', '/admin/drop-generator/:countryKey', '/admin/automations', '/admin/automations/:countryKey'].map((path) => (
            <Route key={path} path={path} element={<LegacyAutomationRedirect />} />
          ))}

          {/* Creative AI (text + sender prompt groups) */}
          <Route
            path="/admin/creative-ai"
            element={
              <ProtectedRoute
                requiredPermissions={['campaign_texts.view', 'campaign_senders.manage']}
              >
                <CampaignTextsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/campaign-texts"
            element={<Navigate to="/admin/creative-ai" replace />}
          />

          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* Developer Documentation (Admin/Dev only) */}
          <Route
            path="/docs"
            element={
              <ProtectedRoute requiredRole="admin">
                <DocsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/:docId"
            element={
              <ProtectedRoute requiredRole="admin">
                <DocsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Forbidden */}
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
